<template>
  <!-- 撮合子订单信息 -->
  <div class="base-card-info">
    <div class="info-card-title relative">
      子订单信息

      <el-button
        v-if="isSaas && duty.orderStatus === ESettlementStatus.UNPAID"
        v-permission="['order:match:adjust:service-amount']"
        type="primary"
        class="absolute right-0"
        @click="showAdjustServiceFee = true"
      >
        调整服务费
      </el-button>
    </div>

    <!-- 表格数据 -->
    <el-table class="normal-table" :border="true" :scrollbar-always-on="true" :data="subOrders" :max-height="450">
      <el-table-column align="left" type="index" label="序号" :min-width="60" />
      <el-table-column align="left" prop="orderDetailNo" label="订单编号" show-overflow-tooltip :min-width="166" />
      <el-table-column align="left" prop="personName" label="收款人" show-overflow-tooltip :min-width="100" />
      <el-table-column align="left" prop="personIdNumber" label="身份证号" :min-width="178" />
      <el-table-column align="left" prop="personTel" label="手机号" :min-width="130" />
      <el-table-column align="left" prop="personBank" label="收款账号" show-overflow-tooltip :min-width="188" />
      <el-table-column align="right" prop="settlementAmount" :min-width="110">
        <template #header>
          <div class="flex items-center justify-end">
            结算金额
            <el-tooltip
              effect="dark"
              placement="top"
              content="结算金额=个人到手金额+税金"
            >
              <svg-icon icon="mingcute:information-line" class="h-icon" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column v-if="isCompany" align="right" prop="serviceAmount" :min-width="110">
        <template #header>
          <div class="flex items-center justify-end">
            服务费
            <el-tooltip
              effect="dark"
              placement="top"
              content="服务费=结算金额*服务费率"
            >
              <svg-icon icon="mingcute:information-line" class="h-icon" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column v-if="!isCompany" align="right" prop="companyServiceAmount" :min-width="130">
        <template #header>
          <div class="flex items-center justify-end">
            企业服务费
            <el-tooltip
              effect="dark"
              placement="top"
              content="服务费=结算金额*服务费率，再根据企业分摊比例，计算企业服务费"
            >
              <svg-icon icon="mingcute:information-line" class="h-icon" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column align="right" prop="receivedAmount" :min-width="130">
        <template #header>
          <div class="flex items-center  justify-end">
            个人到账金额
            <el-tooltip
              effect="dark"
              placement="top"
              content="个人实际到账金额合计"
            >
              <svg-icon icon="mingcute:information-line" class="h-icon" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column v-if="!isCompany" align="right" prop="taxAmount" :min-width="110">
        <template #header>
          <div class="flex items-center justify-end">
            个税金额
            <el-tooltip
              effect="dark"
              placement="top"
              content="此子订单需要支付的税金"
            >
              <svg-icon icon="mingcute:information-line" class="h-icon" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column v-if="isCompany" align="right" prop="taxAmount" :min-width="100">
        <template #header>
          <div class="flex items-center justify-end">
            税金
            <el-tooltip
              effect="dark"
              placement="top"
              content="此子订单需要支付的税金"
            >
              <svg-icon icon="mingcute:information-line" class="h-icon" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column align="right" prop="orderAmount" :min-width="110">
        <template #header>
          <div class="flex items-center  justify-end">
            总金额
            <el-tooltip
              effect="dark"
              :content="`总金额=服务费+到账金额+税金`"
              placement="top"
            >
              <svg-icon icon="mingcute:information-line" class="h-icon" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column align="left" prop="remark" label="备注" show-overflow-tooltip :min-width="120" />
      <el-table-column align="center" prop="orderDetailStatus" label="交易状态" :min-width="90">
        <template #default="{ row }">
          <custom-tag :type="settlementStatusStyle[row.orderDetailStatus]" :label="settlementStatusLabel[row.orderDetailStatus]" />
        </template>
      </el-table-column>
      <el-table-column align="left" prop="detailFailReason" label="失败原因" show-overflow-tooltip :min-width="130" />
      <el-table-column
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
        :width="180"
      >
        <template #default="scope">
          <el-button v-if="scope.row.receiptUrl" link @click="openLinkFile(scope.row.receiptUrl)">
            回单下载
          </el-button>
          <el-button v-if="isSaas && scope.row.contractUrl" link @click="downloadSettlement(scope.row)">
            结算单下载
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <el-pagination
      v-model:current-page="params.page"
      v-model:page-size="params.size"
      class="pt-5 justify-end"
      :page-sizes="[defaultPageSize, 25, 50, 100]"
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      background
      @size-change="onSizeChange"
      @current-change="onCurrentPageChange"
    />
  </div>

  <adjust-sub-order-fee
    :visible="showAdjustServiceFee"
    :data="info"
    :module="module"
    @close="showAdjustServiceFee = false"
    @success="reloadData"
  />
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';
import { downloadLinkFile } from '@core/api';
import { SvgIcon, CustomTag } from '@vue3/components';
import { querySettlementDetailList } from '@core/main';
import { computed, PropType, reactive, ref } from 'vue';
import AdjustSubOrderFee from '../../dialogs/AdjustSubOrderFee.vue';
import { ESettlementStatus, ISettlementDetailListItem, ISettlementDetailListParam, ISettlementOrderDetail, ISettlementOrderDuty, settlementStatusLabel, settlementStatusStyle } from '@core/main/types';

const module = 'cuohe';
const defaultPageSize = 12;

const emit = defineEmits(['reload']);

const route = useRoute();

const props = defineProps({
  term: {
    type: String as PropType<'company' | 'agent' | 'saas' | 'server'>,
    required: true,
  },
  info: {
    type: Object as PropType<ISettlementOrderDetail>,
    required: true,
  },
});

const params = reactive({
  page: 1,
  size: defaultPageSize,
} as ISettlementDetailListParam);

const total = ref(0);
const showAdjustServiceFee = ref(false);
const subOrders = ref<ISettlementDetailListItem[]>([]);

const isCompany = computed(() => props.term === 'company');
const isSaas = computed(() => props.term === 'saas');
const duty = computed(() => props.info?.duty || {} as ISettlementOrderDuty);

async function reloadData() {
  emit('reload');
  await fetchSubOrders(false);
}

async function fetchSubOrders(toastError = true) {
  try {
    const { data, total: all } = await querySettlementDetailList({
      params: {
        ...params,
        orderId: Number(route.query.id),
      },
      module,
    });

    subOrders.value = data;
    total.value = Number(all);
  } catch (error) {
    if (!toastError) {
      throw new Error((error as Error).message);
    } else if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

function onSizeChange(size: number) {
  params.size = size;
  doSearch();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchSubOrders();
}

function doSearch() {
  params.page = 1;
  fetchSubOrders();
}

async function openLinkFile(url: string) {
  try {
    await downloadLinkFile(url);
    ElMessage.success('下载成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

function downloadSettlement(row: ISettlementDetailListItem) {
  window.open(row.contractUrl, '_blank');
}

fetchSubOrders(false);
</script>

<style lang="scss" scoped>
.h-icon {
  @apply text-[var(--color-primary)] ml-1;

  width: 16px !important;
  height: 16px !important;
}
</style>

